import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Stack,
  Box,
  Chip,
  Divider,
  Paper,
  Alert,
} from "@mui/material";
import {
  Person as PersonIcon,
  LocalHospital as SymptomsIcon,
  History as HistoryIcon,
  Medication as MedicationIcon,
  CheckCircle as DiagnosisIcon,
  Assignment,
  LoopOutlined,
} from "@mui/icons-material";
import type { DynamicCase } from "../../types";

interface Props {
  open: boolean;
  patientCase: DynamicCase | null;
  onClose: () => void;
}

export const CaseDetailsDialog: React.FC<Props> = ({
  open,
  patientCase,
  onClose,
}) => {
  if (!patientCase) return null;

  // Helper function to get processing status color
  const getProcessingStatusColor = (status: DynamicCase['processing_status']) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'processing': return '#f59e0b';
      case 'pending': return '#6b7280';
      case 'failed': return '#ef4444';
      case 'review_required': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // Helper function to get difficulty color
  const getDifficultyColor = (level: DynamicCase['difficulty_level']) => {
    switch (level) {
      case 'beginner': return '#22c55e';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <DialogTitle 
        sx={{
          backgroundColor: 'background.default',
          borderBottom: '1px solid #e2e8f0',
          color: 'text.primary',
          textAlign: 'center',
          py: 3,
          '& .MuiTypography-root': {
            fontSize: '1.5rem',
            fontWeight: 600,
          }
        }}
      >
        {patientCase.title}
      </DialogTitle>

      <DialogContent sx={{ p: 4, maxHeight: '70vh', overflow: 'auto' , backgroundColor: 'background.paper'}}>
        <Stack marginTop={4} spacing={4}>

        {/* Processing Status & Difficulty */}
          {/* Patient Info */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: '#3b82f6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Patient Information
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label="Age" 
                  size="small" 
                  sx={{ mr: 2, bgcolor: '#dbeafe', color: '#1e40af' }}
                />
                <Typography variant="body1">{patientCase.age} years old</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label="Gender" 
                  size="small" 
                  sx={{ mr: 2, bgcolor: '#fef3c7', color: '#92400e' }}
                />
                <Typography variant="body1">{patientCase.gender}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label="Occupation" 
                  size="small" 
                  sx={{ mr: 2, bgcolor: '#dcfce7', color: '#166534' }}
                />
                <Typography variant="body1">{patientCase.occupation}</Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Primary Condition */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SymptomsIcon sx={{ mr: 1, color: '#ef4444' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Primary Condition
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {patientCase.primary_condition}
            </Box>
          </Paper>

          {/* Case Preview */}
          {patientCase.preview && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HistoryIcon sx={{ mr: 1, color: '#8b5cf6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Case Preview
              </Typography>
            </Box>
            <Typography paragraph sx={{ mb: 2, lineHeight: 1.6 }}>
              {patientCase.preview}
            </Typography>
          </Paper>
          )}

          {/* Personality Description */}
          {patientCase.personality && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MedicationIcon sx={{ mr: 1, color: '#10b981' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Patient Personaility
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body1">
                  {patientCase.personality}
                </Typography>
              </Box>
            </Stack>
          </Paper>
          )}

          {/* Case Metadata */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MedicationIcon sx={{ mr: 1, color: '#10b981' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Case Metadata
              </Typography>
            </Box>
            <Stack spacing={2}>
              {patientCase.author && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                  Author
                </Typography>
                <Typography variant="body1">
                  {patientCase.author}
                </Typography>
              </Box>
              )}
              {patientCase.peer_reviewer && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                      Peer Reviewer
                    </Typography>
                    <Typography variant="body1">
                      {patientCase.peer_reviewer}
                    </Typography>
                  </Box>
                </>
              )}
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                  Case ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {patientCase.id}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                  Field ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {patientCase.field_id}
                </Typography>
              </Box>
              {patientCase.created_at && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {new Date(patientCase.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, backgroundColor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LoopOutlined sx={{ mr: 1, color: '#3b82f6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Case Status
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label="Processing Status" 
                  size="small" 
                  sx={{ mr: 2, bgcolor: '#dbeafe', color: '#1e40af' }}
                  />
                <Chip
                  label={patientCase.processing_status}
                  size="small"
                  sx={{
                    bgcolor: getProcessingStatusColor(patientCase.processing_status),
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                  />
              </Box>
              {patientCase.difficulty_level && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label="Difficulty" 
                    size="small" 
                    sx={{ mr: 2, bgcolor: '#fef3c7', color: '#92400e' }}
                    />
                  <Chip
                    label={patientCase.difficulty_level}
                    size="small" 
                    sx={{
                      bgcolor: getDifficultyColor(patientCase.difficulty_level),
                      color: 'white',
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}
                    />
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Prompt Instructions */}
          {patientCase.prompt_instructions && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 , backgroundColor: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment sx={{ mr: 1, color: '#8b5cf6' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Prompt Instructions
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  color: 'text.primary',
                  lineHeight: 1.6
                }}
              >
                {patientCase.prompt_instructions}
              </Typography>
            </Paper>
          )}

          {/* Status Alert */}
          {patientCase.processing_status === 'review_required' && (
            <Alert 
              severity="warning" 
              sx={{ 
                '& .MuiAlert-message': { 
                  fontSize: '1rem' 
                }
              }}
            >
              This case requires manual review before it can be used in diagnostic sessions.
            </Alert>
          )}

          {patientCase.processing_status === 'failed' && (
            <Alert 
              severity="error" 
              sx={{ 
                '& .MuiAlert-message': { 
                  fontSize: '1rem' 
                }
              }}
            >
              Case processing failed. Please try reprocessing or contact support.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: 'background.default' , borderTop: '1px solid #e2e8f0' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            bgcolor: 'error.main',
            color: '#FFFFFF',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              bgcolor: 'error.main',
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
